import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { getNicheMeta } from "../niche/nicheMeta";
import { fetchUsersByIds } from "../../services/userQueryService";
import {
  followingIdsFromUser,
  isFollowingListPrivate,
  setFollowingListPrivate,
  toggleFollow,
} from "../../services/followService";
import { getUserProfilePath } from "../../utils/profilePaths";
import { formatCount } from "../../utils/formatCount";
import AvatarWithTrophy from "./AvatarWithTrophy";
import "./FollowingOverlay.css";

function firstNameOf(user) {
  const raw = user?.displayName || user?.handle || user?.username || "This user";
  return String(raw).split(" ")[0];
}

export function FollowingStatPill({ count, isPrivate, onClick }) {
  return (
    <button
      type="button"
      className={`fl-stat-tab${isPrivate ? " private-tab" : ""}`}
      onClick={onClick}
    >
      <span className="fl-stat-num">{formatCount(count)}</span>
      <span className="fl-stat-lbl">
        Following
        {isPrivate ? <span className="fl-lock-mini">🔒</span> : null}
      </span>
    </button>
  );
}

export default function FollowingOverlay({
  open,
  onClose,
  profileUser,
  isOwner,
  currentUser,
  onPrivacyChange,
  onOwnerUnfollow,
}) {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [people, setPeople] = useState([]);
  const [loading, setLoading] = useState(false);
  const [viewerFollowing, setViewerFollowing] = useState(() => new Set());
  const [busyId, setBusyId] = useState(null);
  const [listPrivate, setListPrivate] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingPrivate, setPendingPrivate] = useState(null);
  const [privacyBusy, setPrivacyBusy] = useState(false);

  const followingIds = followingIdsFromUser(profileUser);
  const followingKey = followingIds.join("|");
  const showList = isOwner || !listPrivate;

  useEffect(() => {
    if (!open) return;
    setListPrivate(isFollowingListPrivate(profileUser));
    setQuery("");
    setConfirmOpen(false);
    setPendingPrivate(null);
  }, [open, profileUser]);

  useEffect(() => {
    if (!open || !showList) {
      setPeople([]);
      return undefined;
    }
    const ids = followingKey ? followingKey.split("|") : [];
    let cancelled = false;
    setLoading(true);
    fetchUsersByIds(ids)
      .then((rows) => {
        if (!cancelled) setPeople(rows);
      })
      .catch((err) => {
        console.error("Following list load error:", err);
        if (!cancelled) setPeople([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [open, showList, followingKey]);

  useEffect(() => {
    if (!open || !currentUser) {
      setViewerFollowing(new Set());
      return;
    }
    setViewerFollowing(new Set(followingIdsFromUser(currentUser)));
  }, [open, currentUser]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") {
        if (confirmOpen) {
          setConfirmOpen(false);
          setPendingPrivate(null);
        } else {
          onClose();
        }
      }
    };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, confirmOpen, onClose]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return people;
    return people.filter((p) => {
      const niche = getNicheMeta(p.niche).label.toLowerCase();
      const name = (p.displayName || "").toLowerCase();
      const handle = (p.handle || p.username || "").toLowerCase();
      return name.includes(q) || handle.includes(q) || niche.includes(q);
    });
  }, [people, query]);

  if (!open || !profileUser) return null;

  const requestPrivacyChange = () => {
    if (!isOwner || privacyBusy) return;
    setPendingPrivate(!listPrivate);
    setConfirmOpen(true);
  };

  const cancelPrivacyChange = () => {
    setPendingPrivate(null);
    setConfirmOpen(false);
  };

  const confirmPrivacyChange = async () => {
    if (!isOwner || pendingPrivate == null || !currentUser?.uid) return;
    setPrivacyBusy(true);
    try {
      await setFollowingListPrivate(currentUser.uid, pendingPrivate);
      setListPrivate(pendingPrivate);
      onPrivacyChange?.(pendingPrivate);
      setConfirmOpen(false);
      setPendingPrivate(null);
    } catch (err) {
      console.error("Following privacy update failed:", err);
    } finally {
      setPrivacyBusy(false);
    }
  };

  const handleFollowClick = async (person) => {
    if (!currentUser) {
      navigate("/welcome?auth=signin");
      return;
    }
    const id = person.uid || person.id;
    if (!id || id === currentUser.uid || busyId) return;
    const currently = viewerFollowing.has(id);
    setBusyId(id);
    try {
      const next = await toggleFollow({
        followerId: currentUser.uid,
        creatorId: id,
        isFollowing: currently,
        creatorUsername: person.handle || person.username || "",
        creatorDisplayName: person.displayName,
        creatorAvatar: person.avatar || person.avatarUrl,
      });
      setViewerFollowing((prev) => {
        const copy = new Set(prev);
        if (next) copy.add(id);
        else copy.delete(id);
        return copy;
      });
      if (isOwner && !next) {
        setPeople((prev) => prev.filter((p) => (p.uid || p.id) !== id));
        onOwnerUnfollow?.(id);
      }
    } catch (err) {
      console.error("Following list toggle failed:", err);
    } finally {
      setBusyId(null);
    }
  };

  const makingPrivate = pendingPrivate === true;

  return (
    <div className="follow-overlay open" onClick={onClose} role="presentation">
      <div
        className="follow-sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby="fs-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="fs-handle" />
        <div className="fs-head">
          <div className="fs-title" id="fs-title">Following ({followingIds.length})</div>
          <button type="button" className="fs-close" onClick={onClose} aria-label="Close">✕</button>
        </div>

        {isOwner && (
          <div className="fs-privacy-row">
            <button
              type="button"
              className={`fs-checkbox${listPrivate ? " checked" : ""}`}
              onClick={requestPrivacyChange}
              aria-pressed={listPrivate}
              aria-label="Keep my Following list private"
            >
              {listPrivate ? (
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              ) : null}
            </button>
            <button type="button" className="fs-privacy-copy" onClick={requestPrivacyChange}>
              <div className="fs-privacy-label">Keep my Following list private</div>
              <div className="fs-privacy-sub">
                {listPrivate ? "Only you can see this list right now" : "Anyone can currently see who you follow"}
              </div>
            </button>
          </div>
        )}

        {showList ? (
          <>
            <div className="fs-search">
              <input
                className="fs-search-input"
                placeholder="Search this list..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>
            <div className="fs-list">
              {loading && <div className="fs-empty">Loading…</div>}
              {!loading && filtered.length === 0 && (
                <div className="fs-empty">
                  {followingIds.length === 0 ? "Not following anyone yet." : "No matches in this list."}
                </div>
              )}
              {!loading && filtered.map((person) => {
                const id = person.uid || person.id;
                const handle = person.handle || person.username || id;
                const niche = getNicheMeta(person.niche);
                const isSelf = currentUser?.uid === id;
                const isFollowing = viewerFollowing.has(id);
                const to = getUserProfilePath(person);
                return (
                  <div key={id} className="fs-item">
                    <Link to={to || "#"} className="fs-av-link" onClick={onClose}>
                      <AvatarWithTrophy
                        src={person.avatar || person.avatarUrl}
                        alt={person.displayName || handle}
                        imgClassName="fs-av"
                        size="sm"
                        pinnedTrophy={person.pinnedTrophy}
                      />
                    </Link>
                    <Link to={to || "#"} className="fs-body" onClick={onClose}>
                      <div className="fs-name">{person.displayName || handle}</div>
                      <div className="fs-meta">
                        <span className="fs-niche-dot" />
                        {niche.label}
                      </div>
                    </Link>
                    {!isSelf && (
                      <button
                        type="button"
                        className={`fs-follow-btn${isFollowing ? " following" : ""}`}
                        disabled={busyId === id}
                        onClick={() => handleFollowClick(person)}
                      >
                        {isFollowing ? "Following" : "Follow"}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        ) : (
          <div className="fs-private-state">
            <div className="fs-private-icon">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <rect x="4" y="10" width="16" height="10" rx="2" />
                <path d="M8 10V7a4 4 0 018 0v3" />
              </svg>
            </div>
            <div className="fs-private-title">This list is private</div>
            <div className="fs-private-sub">
              {firstNameOf(profileUser)} has chosen to keep who they follow to themselves.
            </div>
          </div>
        )}
      </div>

      {confirmOpen && (
        <div className="confirm-dialog-overlay open" onClick={cancelPrivacyChange} role="presentation">
          <div className="confirm-dialog" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
            <div className="cd-icon">
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <rect x="4" y="10" width="16" height="10" rx="2" />
                <path d="M8 10V7a4 4 0 018 0v3" />
              </svg>
            </div>
            <div className="cd-title">{makingPrivate ? "Make this private?" : "Make this public?"}</div>
            <div className="cd-sub">
              {makingPrivate
                ? "Only you will be able to see who you follow. You can change this back anytime."
                : "Anyone who visits your profile will be able to see who you follow."}
            </div>
            <div className="cd-actions">
              <button type="button" className="cd-btn cd-btn-cancel" onClick={cancelPrivacyChange}>Cancel</button>
              <button
                type="button"
                className="cd-btn cd-btn-confirm"
                onClick={confirmPrivacyChange}
                disabled={privacyBusy}
              >
                {privacyBusy ? "Saving…" : "Confirm"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
